import React, { useState } from 'react';
import { AppButton, Box, Inscription } from '@common-fe/common-fe';

import StatusIcon from '@/components/elements/StatusIcon';
import { ProcessingStatusType, TransactionApprovalCode, TransactionData, TransactionStatus } from '@/modules/transaction/transaction.types';

import { IconWrapper, Wrapper } from '../../TransactionNotification.styles';

import ClaimRepaymentModal from './ClaimRepaymentModal';

const isFullInitialRepayment = (claimStatus?: TransactionStatus, data?: TransactionData) => claimStatus === TransactionStatus?.RepaymentRequired
  && data?.processingStatus === ProcessingStatusType?.RepaymentRequired;
const isPartialInitialRepayment = (claimStatus?: TransactionStatus, data?: TransactionData) => claimStatus === TransactionStatus?.PartiallyRepaid
  && data?.processingStatus === ProcessingStatusType?.ActionRequired
  && data?.processingSubstatusType === TransactionApprovalCode?.PARTIAL_REPAYMENT;
const isRepaymentRejected = (claimStatus?: TransactionStatus, data?: TransactionData) => claimStatus === TransactionStatus?.NeedsHelp
  && data?.processingStatus === ProcessingStatusType?.ActionRequired
  && data?.processingSubstatusType === TransactionApprovalCode.REPAYMENT_REJECTED;

const isRepayment = (claimStatus?: TransactionStatus, data?: TransactionData) => isFullInitialRepayment(claimStatus, data)
  || isPartialInitialRepayment(claimStatus, data);

const isRepaymentAgain = (claimStatus?: TransactionStatus, data?: TransactionData) => isRepaymentRejected(claimStatus, data);

export const getClaimRepaymentMessageType = (data?: TransactionData) => {
  const claimStatus = data?.statuses?.[0]?.status;

  if (isRepayment(claimStatus, data)) return { isRepayment: true };

  if (isRepaymentAgain(claimStatus, data)) return { isRepaymentAgain: true };
};

interface Props {
  data?: TransactionData;
  isRepaymentAgain?: boolean;
}

const ClaimRepaymentMessage: React.FC<Props> = ({ data, isRepaymentAgain }) => {
  const [isShown, setIsShown] = useState(false);

  return (
    <Wrapper
      error
      data-testid="claim-repayment-message"
      flex-direction="column"
      background="canvas"
      border={{
        size: 'small',
        color: 'dangerBorder',
      }}
      pad="l"
    >
      <Box margin={{ left: 'spacing32' }}>
        <IconWrapper><StatusIcon status={TransactionStatus.Denied} /></IconWrapper>
        <Inscription color="textBody" size="16px" weight="bold" lineHeight="22px">
          {isRepaymentAgain
            ? 'Repayment was rejected.'
            : 'This service is not an eligible expense for your plan.'}
        </Inscription>
        <Inscription margin={{ top: 'spacing8' }} color="textBody" size="16px" lineHeight="22px">
          This claim requires repayment. If you think we got it wrong, you can appeal and add additional documentation.
        </Inscription>
      </Box>
      <Box margin={{ top: 'spacing12' }}>
        <AppButton
          testId="ClaimRepaymentMessage"
          style={{
            minWidth: '130px',
          }}
          onClick={() => setIsShown(true)}
        >
          {isRepaymentAgain ? 'Retry Repayment' : 'Repay'}
        </AppButton>
      </Box>

      {isShown ? (
        <ClaimRepaymentModal
          data={data}
          onClose={() => setIsShown(false)}
          isRejectionFlow={isRepaymentAgain}
        />
      ) : null}
    </Wrapper>
  );
};

export default ClaimRepaymentMessage;
